// @flow

import React, { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import {
  Box,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatDateDisplayLocal } from '@fortress-technology-solutions/fortress-component-library/utils';
import { DASHES } from '@fortress-technology-solutions/fortress-component-library/constants';
import {
  FloppyIcon,
  PencilIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import editCertMessages from '../messages';
import useReactHookFormProps from '../../../../hooks/useReactHookFormProps';
import type {
  EditGrossRentCertificationModalCert,
  EditGrossRentCertificationMutationVariables,
} from '../types';

type EditGrossRentCertificationModalProps = {
  editGrossRentCertificationMutation: {
    mutate: (payload: EditGrossRentCertificationMutationVariables) => void,
    isLoading: boolean,
  },
  handleClose: () => void,
  modalState: {
    affordableQualificationId?: string,
    grossRentCert?: EditGrossRentCertificationModalCert,
  },
  modalType: ?string,
  open: boolean,
};

const EditGrossRentCertificationModal = ({
  editGrossRentCertificationMutation: {
    mutate: editGrossRentCertificationMutate,
    isLoading: editGrossRentCertificationMutationIsLoading,
  },
  handleClose,
  modalState,
  modalType,
  open,
}: EditGrossRentCertificationModalProps) => {
  const ReactHookFormProps = useReactHookFormProps({ mode: 'onChange' });
  const { reset, getValues, isValid, isDirty } = ReactHookFormProps;

  const grossRentCert = modalState?.grossRentCert;
  const { unit, headOfHousehold, grEffectiveDate, prevMissingCert } =
    grossRentCert ?? {};

  useEffect(() => {
    if (modalType !== 'editCert') {
      return;
    }

    reset({
      priorCertificationDate: prevMissingCert?.priorCertificationDate
        ? moment(prevMissingCert.priorCertificationDate)
        : null,
      prevHUDGrossRentLimit: prevMissingCert?.prevHUDGrossRentLimit,
      adjustedAnnualIncome: prevMissingCert?.adjustedAnnualIncome,
      ttp: prevMissingCert?.ttp,
      qualifyForRentOverride: prevMissingCert?.qualifyForRentOverride
        ? 'yes'
        : 'no',
      TTPbeforeOverride: prevMissingCert?.TTPbeforeOverride,
    });
  }, [modalType, prevMissingCert, reset]);

  const effectiveDate = grEffectiveDate
    ? formatDateDisplayLocal(grEffectiveDate)
    : DASHES;

  const { modalProps, formProps } = useMemo(() => {
    return {
      modalProps: {
        title: (
          <FormattedMessage
            {...editCertMessages.editGrossRentCertificationModalTitle}
          />
        ),
        icon: <PencilIcon />,
        TitleProps: {
          sx: (theme) => ({
            ...theme.typography.titleLarge,
          }),
        },
        actionsProps: [
          {
            children: 'Cancel',
            onClick: handleClose,
          },
          {
            children: 'Save',
            submitButton: true,
            startIcon: <FloppyIcon />,
            isSubmitting: editGrossRentCertificationMutationIsLoading,
            disabled: !isValid || !isDirty,
            onClick: () => {
              const values = getValues();
              editGrossRentCertificationMutate({
                affordableQualificationId:
                  modalState.affordableQualificationId ?? '',
                prevMissingCert: {
                  priorCertificationDate: values.priorCertificationDate,
                  prevHUDGrossRentLimit: values.prevHUDGrossRentLimit,
                  adjustedAnnualIncome: values.adjustedAnnualIncome,
                  ttp: values.ttp,
                  qualifyForRentOverride:
                    values.qualifyForRentOverride === 'yes',
                  TTPbeforeOverride: values.TTPbeforeOverride,
                },
              });
            },
          },
        ],
      },
      formProps: {
        fields: [
          {
            name: 'unitSummary',
            variant: 'custom',
            component: (
              <Box display="flex" flexDirection="column" width="100%">
                <Typography variant="titleMedium" display="block">
                  Unit {unit} - {headOfHousehold}
                </Typography>
                <Typography variant="bodySmall" display="block">
                  Gross Rent Effective Date: {effectiveDate}
                </Typography>
              </Box>
            ),
            GridProps: { xs: 12 },
          },
          {
            name: 'priorCertificationInfo',
            variant: 'custom',
            component: (
              <Typography variant="titleMedium" display="block">
                Prior Certification Information
              </Typography>
            ),
            GridProps: { xs: 12 },
          },
          {
            variant: 'date',
            name: 'priorCertificationDate',
            label: 'Certification Date',
            helperText: 'Required',
            rules: { required: true },
            GridProps: { xs: 6 },
          },
          {
            variant: 'number',
            name: 'prevHUDGrossRentLimit',
            label: 'Prior HUD Gross Rent',
            adornment: '$',
            helperText: 'Required',
            rules: { required: true, min: 0 },
            GridProps: { xs: 6 },
          },
          {
            variant: 'number',
            name: 'adjustedAnnualIncome',
            label: 'Adjusted Annual Income',
            adornment: '$',
            helperText: 'Required',
            rules: { required: true, min: -1 },
            GridProps: { xs: 6 },
          },
          {
            variant: 'number',
            name: 'ttp',
            label: 'Total Tenant Payment',
            adornment: '$',
            helperText: 'Required',
            rules: { required: true, min: 0 },
            GridProps: { xs: 6 },
          },
          {
            variant: 'radioGroup',
            name: 'qualifyForRentOverride',
            label: 'Unit Qualifies for Rent Override',
            helperText: 'Required',
            options: [
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ],
            sx: { flexDirection: 'row' },
            rules: { required: true },
            GridProps: { xs: 6 },
          },
          {
            variant: 'number',
            name: 'TTPbeforeOverride',
            label: 'TTP Before Override',
            adornment: '$',
            helperText: 'Required',
            watchFields: ['qualifyForRentOverride'],
            disabled: ({ getValues: getFormValues }) =>
              getFormValues('qualifyForRentOverride') !== 'yes',
            rules: {
              required: ({ getValues: getFormValues }) =>
                getFormValues('qualifyForRentOverride') === 'yes',
            },
            GridProps: { xs: 6 },
          },
        ],
      },
    };
  }, [
    editGrossRentCertificationMutate,
    editGrossRentCertificationMutationIsLoading,
    effectiveDate,
    getValues,
    handleClose,
    headOfHousehold,
    isDirty,
    isValid,
    modalState.affordableQualificationId,
    unit,
  ]);

  if (modalType !== 'editCert') {
    return null;
  }

  return (
    <Modal open={open} onClose={handleClose} {...modalProps}>
      <Form ReactHookFormProps={ReactHookFormProps} {...formProps} />
    </Modal>
  );
};

export default EditGrossRentCertificationModal;
